import React, { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ArrowRight } from 'lucide-react';
import {
  Heading,
  HStack,
  Text,
} from '@ayble-health/dashboard-components';
import { useAuth } from '../../hooks';
import { useProviderAppointments } from '../../api/providerHome/useProviderAppointments';
import { useProviderTasks } from '../../api/providerHome/useProviderTasks';
import { AppointmentsColumn } from './AppointmentsColumn';
import { TasksColumn } from './TasksColumn';
import { CountTabBar } from './CountTabs';
import { ACTIVE_BAR, INK, SUBTLE } from './tokens';
import { usePrototypeState } from '../PrototypeBar/PrototypeStateContext';

const { REACT_APP_HEALTHIE_URL } = process.env;

/**
 * Provider Home Page: the Care.OS launch screen. Left column is the provider's
 * day of visits, right column is their tasks. See plans/careos-provider-home.
 *
 * Layout: a fixed-height sticky header (greeting + column headings + tab bars)
 * above two independently-scrolling content columns.
 */
export const ProviderHome = () => {
  const { user } = useAuth();
  const {
    today: fetchedToday,
    tomorrow: fetchedTomorrow,
  } = useProviderAppointments();
  const { tasks: fetchedTasks } = useProviderTasks();
  const [apptTabIndex, setApptTabIndex] = useState(0);

  // Prototype "State" control: 'empty' withholds the fixture data so both
  // columns fall through to their own empty states, and the tab counts read
  // zero. Nothing else about the page changes.
  const { homeState } = usePrototypeState();
  const isEmpty = homeState === 'empty';
  const today = isEmpty ? [] : fetchedToday;
  const tomorrow = isEmpty ? [] : fetchedTomorrow;
  const tasks = isEmpty ? [] : fetchedTasks;

  const lastName = user?.last_name ?? '';
  const dateLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const apptTabs = [
    { label: 'Today', count: today.length },
    { label: 'Tomorrow', count: tomorrow.length },
  ];
  const taskTabs = [{ label: 'All', count: tasks.length }];
  const apptVisits = apptTabIndex === 0 ? today : tomorrow;

  const ManageCalendar = (
    <HStack
      gap={2}
      align="center"
      bg="white"
      borderWidth="1px"
      borderColor="bluegrey"
      borderRadius="8px"
      px={3}
      py="6px"
      cursor="pointer"
      onClick={() => window.open(REACT_APP_HEALTHIE_URL, '_blank', 'noopener')}
      _hover={{ bg: 'feather' }}
    >
      <Text color="seafoam" fontWeight={500} fontSize="sm" whiteSpace="nowrap">
        Manage Calendar
      </Text>
      <ArrowRight size={16} color={ACTIVE_BAR} />
    </HStack>
  );

  return (
    <Box h="100vh" w="100%" bg="canvas" display="flex" flexDirection="column">

      {/* ── STICKY HEADER ────────────────────────────────────────────────── */}
      <Box flexShrink={0} bg="canvas">

        {/* Greeting row */}
        <Flex
          justify="space-between"
          align="flex-start"
          gap={4}
          px={8}
          pt={6}
          pb={6}
          borderBottomWidth="1px"
          borderColor="cloud"
        >
          <HStack gap={3} align="baseline">
            <Heading size="lg" color={INK}>
              Good morning, Dr. {lastName}
            </Heading>
            <Text color={SUBTLE} fontSize="md">
              {dateLabel}
            </Text>
          </HStack>
        </Flex>

        {/* Column headings + tab bars */}
        <Flex>
          <Box flex={1.25} minW={0} px={8} pt={6} borderRightWidth="1px" borderColor="cloud">
            <HStack justify="space-between" align="center" minH="36px" mb={3}>
              <Heading size="sm" color={INK}>
                Appointments
              </Heading>
              {ManageCalendar}
            </HStack>
            <CountTabBar
              tabs={apptTabs}
              activeIndex={apptTabIndex}
              setActiveIndex={setApptTabIndex}
              mb={0}
            />
          </Box>
          <Box flex={1} minW={0} px={8} pt={6}>
            <Box display="flex" alignItems="center" minH="36px" mb={3}>
              <Heading size="sm" color={INK}>
                Tasks
              </Heading>
            </Box>
            <CountTabBar
              tabs={taskTabs}
              activeIndex={0}
              setActiveIndex={() => undefined}
              mb={0}
            />
          </Box>
        </Flex>

      </Box>

      {/* ── SCROLLABLE CONTENT ───────────────────────────────────────────── */}
      <Flex flex={1} overflow="hidden">
        <Box
          flex={1.25}
          minW={0}
          px={8}
          pt={4}
          overflowY="auto"
          borderRightWidth="1px"
          borderColor="cloud"
        >
          <AppointmentsColumn visits={apptVisits} showNowLine={apptTabIndex === 0} />
        </Box>
        <Box flex={1} minW={0} px={8} pt={4} overflowY="auto">
          <TasksColumn tasks={tasks} />
        </Box>
      </Flex>

    </Box>
  );
};
